import { useCallback, useEffect, useMemo, useRef } from 'react';
import type { ArtifactKind } from '@inference.engine/widgets';
import { useQueryClient } from '@tanstack/react-query';
import {
  getArtifactRowKey,
  PROJECT_ARTIFACT_QUERY_KEYS,
  type ListProjectArtifactsResponse,
} from '@/entities/project-artifact';
import { ARTIFACT_KIND_PALETTE } from '../../../../library.constants';
import { useArtifactPreviewStore } from '../../../../store';

/** BE widens `artifactKind` to `string`; narrow to known palette keys, falling back to `misc`. */
function resolveKind(artifactKind: string): ArtifactKind {
  if (artifactKind in ARTIFACT_KIND_PALETTE) {
    return artifactKind as ArtifactKind;
  }
  return 'misc';
}

/**
 * Prev/next + keyboard navigation for the artifact preview modal.
 *
 * Reads the ordered artifact list from the same TanStack Query cache the
 * Library grid populates (`['project', projectId, 'artifacts', 'list', query]`
 * — see `PROJECT_ARTIFACT_QUERY_KEYS.list` in `entities/project-artifact`).
 * No duplicate fetch is triggered: we query the cache via `getQueriesData`
 * and reuse whichever cached page contains the currently open artifact.
 *
 * The Library grid is paginated, so navigation is scoped to the page that
 * holds `openArtifactId`. At a page boundary the corresponding direction
 * returns `null` and arrows disable (no wrap, no cross-page jump). See the
 * "Surfaced during implementation" section of the story draft
 * for the documented gap.
 *
 * Boundaries are no-ops; the keyboard listener is bound to `document` only
 * while the modal is open (`openArtifactId !== null`).
 */
export interface UseArtifactNavigationResult {
  prevId: string | null;
  nextId: string | null;
  goPrev: () => void;
  goNext: () => void;
}

export function useArtifactNavigation(projectId: string | undefined): UseArtifactNavigationResult {
  const queryClient = useQueryClient();
  const openArtifactId = useArtifactPreviewStore((s) => s.openArtifactId);
  const open = useArtifactPreviewStore((s) => s.open);
  // Library publishes its active filter/sort/page query into the store; we
  // resolve prev/next from the EXACT cache entry the user is viewing, not
  // by guessing across stale prefix-matched pages.
  const listQuery = useArtifactPreviewStore((s) => s.listQuery);

  const { prevId, prevName, prevKind, prevKey, nextId, nextName, nextKind, nextKey } =
    useMemo(() => {
      const empty = {
        prevId: null as string | null,
        prevName: null as string | null,
        prevKind: null as ArtifactKind | null,
        prevKey: null as string | null,
        nextId: null as string | null,
        nextName: null as string | null,
        nextKind: null as ArtifactKind | null,
        nextKey: null as string | null,
      };

      if (!projectId || openArtifactId === null) return empty;

      const targetKey = PROJECT_ARTIFACT_QUERY_KEYS.list(projectId, listQuery ?? undefined);
      const page = queryClient.getQueryData<ListProjectArtifactsResponse>(targetKey);
      const items = page?.items;
      if (!items) return empty;

      const index = items.findIndex((item) => getArtifactRowKey(item) === openArtifactId);
      if (index === -1) return empty;

      const prev = index > 0 ? items[index - 1] : null;
      const next = index < items.length - 1 ? items[index + 1] : null;

      return {
        prevId: prev ? getArtifactRowKey(prev) : null,
        prevName: prev?.artifactName ?? null,
        prevKind: prev ? resolveKind(prev.artifactKind) : null,
        prevKey: prev?.artifactKey ?? null,
        nextId: next ? getArtifactRowKey(next) : null,
        nextName: next?.artifactName ?? null,
        nextKind: next ? resolveKind(next.artifactKind) : null,
        nextKey: next?.artifactKey ?? null,
      };
    }, [queryClient, projectId, openArtifactId, listQuery]);

  const goPrev = useCallback(() => {
    if (prevId === null || prevName === null || prevKind === null) return;
    open(prevId, prevName, prevKind, prevKey);
  }, [open, prevId, prevName, prevKind, prevKey]);

  const goNext = useCallback(() => {
    if (nextId === null || nextName === null || nextKind === null) return;
    open(nextId, nextName, nextKind, nextKey);
  }, [open, nextId, nextName, nextKind, nextKey]);

  // Stabilize callbacks via refs so the listener only resubscribes when the
  // modal opens/closes — not on every render where `goPrev` / `goNext`
  // change identity (which is every state update in this hook).
  const goPrevRef = useRef(goPrev);
  const goNextRef = useRef(goNext);
  useEffect(() => {
    goPrevRef.current = goPrev;
    goNextRef.current = goNext;
  });

  useEffect(() => {
    if (openArtifactId === null) return;

    function handleKeyDown(event: KeyboardEvent) {
      // Skip when focus is in editable content (widget bodies can render
      // inputs/textareas/contenteditable nodes) so arrow keys move the caret
      // instead of hijacking artifact navigation.
      const target = event.target;
      if (target instanceof HTMLElement) {
        const tag = target.tagName;
        if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || target.isContentEditable) {
          return;
        }
      }
      if (event.key === 'ArrowLeft') {
        goPrevRef.current();
      } else if (event.key === 'ArrowRight') {
        goNextRef.current();
      }
    }

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [openArtifactId]);

  return { prevId, nextId, goPrev, goNext };
}
